import {observer} from "mobx-react";
import React from "react";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import DropDeskIcon from "@dropDesk/storage/icons";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import {hexToRGBA} from "@dropDesk/utils/helpers/colors";
import {ComparativeRankItem} from "@dropDesk/domain/entities/reports/report_ticket_comparative/comparative_metric.entity";
import {
    DirectionIcon,
    formatVariationPercent,
    getPillTone,
} from "@dropDesk/presentation/components/metrics_cards/variation_helpers";
import {SharedColors} from "@dropDesk/domain/entities/theme/app_colors";
import {
    EmptyState,
    HeadCell,
    HighlightRow,
    HighlightText,
    IconCircle,
    NameCell,
    NameText,
    RankCell,
    SkeletonBone,
    Table,
    TableCard,
    TableHead,
    TableHeader,
    TableRow,
    TableTitle,
    ValueCell,
    VariationCell,
    VariationPill,
} from "./styled";

const RankingTable = observer(({
    title,
    items,
    rowIcon,
    nameHeader,
    highlight,
    loading = false,
}: {
    title: string;
    items: ComparativeRankItem[];
    rowIcon: string;
    nameHeader: string;
    highlight?: string;
    loading?: boolean;
}) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;
    const softBorder = hexToRGBA(colors.border, 0.7);
    const accentSoft = hexToRGBA(colors.accent, 0.12);
    const skeletonBackground = hexToRGBA(colors.border, 0.55);
    const skeletonHighlight = hexToRGBA(colors.hint, 0.35);

    return (
        <TableCard background={colors.onBackground} border={colors.border}>
            <TableHeader>
                <TableTitle color={colors.text}>{title}</TableTitle>
            </TableHeader>
            {!loading && items.length === 0 && (
                <EmptyState color={colors.hint}>Sem dados para o período selecionado.</EmptyState>
            )}
            {(loading || items.length > 0) && (
                <Table>
                    <TableHead border={softBorder}>
                        <HeadCell color={colors.hint}>#</HeadCell>
                        <HeadCell color={colors.hint}>{nameHeader}</HeadCell>
                        <HeadCell color={colors.hint} style={{textAlign: 'right'}}>Atual</HeadCell>
                        <HeadCell color={colors.hint} style={{textAlign: 'right'}}>Anterior</HeadCell>
                        <HeadCell color={colors.hint} style={{textAlign: 'right'}}>Variação</HeadCell>
                    </TableHead>
                    {loading && Array.from({length: 5}).map((_, index) => (
                        <TableRow key={index} border={softBorder}>
                            <SkeletonBone
                                background={skeletonBackground}
                                highlight={skeletonHighlight}
                                width="14px"
                                height="14px"
                            />
                            <NameCell>
                                <SkeletonBone
                                    background={skeletonBackground}
                                    highlight={skeletonHighlight}
                                    width="28px"
                                    height="28px"
                                    borderRadius="50%"
                                />
                                <SkeletonBone
                                    background={skeletonBackground}
                                    highlight={skeletonHighlight}
                                    width={`${72 - (index * 6)}%`}
                                    height="13px"
                                />
                            </NameCell>
                            <SkeletonBone
                                background={skeletonBackground}
                                highlight={skeletonHighlight}
                                width="32px"
                                height="13px"
                                style={{justifySelf: 'end'}}
                            />
                            <SkeletonBone
                                background={skeletonBackground}
                                highlight={skeletonHighlight}
                                width="32px"
                                height="13px"
                                style={{justifySelf: 'end'}}
                            />
                            <SkeletonBone
                                background={skeletonBackground}
                                highlight={skeletonHighlight}
                                width="64px"
                                height="24px"
                                borderRadius="12px"
                                style={{justifySelf: 'end'}}
                            />
                        </TableRow>
                    ))}
                    {!loading && items.map((item, index) => {
                        const pillTone = getPillTone(item.direction, item.isImprovement, colors.hint);
                        return (
                            <TableRow key={item.id ?? `${item.name}-${index}`} border={softBorder}>
                                <RankCell color={colors.hint}>{index + 1}</RankCell>
                                <NameCell>
                                    <IconCircle background={accentSoft}>
                                        <DropDeskIcon icon={rowIcon} size={14} color={colors.accent}/>
                                    </IconCircle>
                                    <NameText color={colors.text} title={item.name}>{item.name}</NameText>
                                </NameCell>
                                <ValueCell color={colors.text}>{Math.round(item.current).toLocaleString('pt-BR')}</ValueCell>
                                <ValueCell color={colors.hintInverse}>{Math.round(item.previous).toLocaleString('pt-BR')}</ValueCell>
                                <VariationCell>
                                    <VariationPill color={pillTone.color} background={pillTone.background}>
                                        <DirectionIcon direction={item.direction} color={pillTone.color}/>
                                        {formatVariationPercent(item.variationPercent)}
                                    </VariationPill>
                                </VariationCell>
                            </TableRow>
                        );
                    })}
                </Table>
            )}
            {!!highlight && !loading && (
                <HighlightRow
                    border={softBorder}
                    background={'transparent'}
                >
                    <DropDeskIcon icon={ICONS_DROPDESK.check} size={14} color={SharedColors.success}/>
                    <HighlightText color={colors.hintInverse}>{highlight}</HighlightText>
                </HighlightRow>
            )}
        </TableCard>
    );
});

export default RankingTable;
